import {
  CheckCircleFilled,
  CopyOutlined,
  SearchOutlined,
  ShareAltOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { App, Button, Empty, Input, Modal } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HoverImagePreview } from '../../../shared/HoverImagePreview';
import {
  type CaseShowcaseFixture,
  type CaseShowcaseRecord,
  createCaseShowcaseFixture,
} from '../mock/caseShowcase';
import {
  readCaseShowcaseMockCart,
  readCaseShowcaseMockCases,
  readCaseShowcaseMockPlanRelations,
  saveCaseShowcaseMockCart,
} from '../mock/caseShowcaseSession';
import { createCaseShowcaseCartShareToken } from '../mock/caseShowcaseShare';
import { formatMoney } from '../mock/catalog';
import { useCpq } from '../state/CpqContext';
import { requireDeviceSku } from '../state/deviceCatalog';
import { createQuote, saveDraft } from '../state/machine';
import { canUseActivePermissionAction } from '../state/permissionPolicy';
import {
  mergeQuoteItemSelections,
  type QuoteItemSelection,
} from '../state/quoteItems';
import {
  createPricingBaseSnapshot,
  planProductPrice,
  planProductSubtotal,
} from '../state/quoteTotals';
import styles from '../styles.module.css';

const CASE_CATEGORY_PREVIEW_COUNT = 9;

interface CaseQuoteItem {
  lineId: string;
  deviceId: string;
  name: string;
  sku: string;
  image: string;
  category: string;
  quantity: number;
  unitPrice: number;
  packageRelation?: {
    relationId: string;
    packageId: string;
    packageName: string;
    packageRevisionId?: string;
    relationQuantity: number;
    packageItemPrice: number | null;
    replacementPoolId?: string | null;
  };
}

interface CaseCartRow {
  caseRecordId: string;
  planId: string;
  items: CaseQuoteItem[];
}

function matchesCaseCategory(record: CaseShowcaseRecord, category: string) {
  if (category === '全部') return true;
  return (
    record.category === category ||
    record.tags.includes(category) ||
    record.name.includes(category)
  );
}

function planItems(
  catalog: CaseShowcaseFixture['catalog'],
  planId: string,
): CaseQuoteItem[] {
  return catalog.packages
    .filter((pkg) => pkg.planIds.includes(planId))
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .flatMap((pkg) =>
      [...pkg.productLinks]
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .map((relation) => ({ pkg, relation })),
    )
    .flatMap(({ pkg, relation }) => {
      const device = catalog.devices.find(
        (record) => record.id === relation.deviceId,
      );
      return device
        ? [
            {
              lineId: relation.relationId,
              deviceId: device.id,
              name: device.name,
              sku: requireDeviceSku(device),
              image: device.image,
              category: device.category,
              quantity: relation.quantity,
              unitPrice: createPricingBaseSnapshot('rental', {
                packageItemPrice: relation.packageItemPrice,
              }).amount,
              packageRelation: {
                relationId: relation.relationId,
                packageId: pkg.id,
                packageName: pkg.name,
                ...((relation.packageRevisionId ?? pkg.currentRevisionId)
                  ? {
                      packageRevisionId:
                        relation.packageRevisionId ?? pkg.currentRevisionId,
                    }
                  : {}),
                relationQuantity: relation.quantity,
                packageItemPrice: relation.packageItemPrice,
                replacementPoolId: relation.replacementPoolId,
              },
            },
          ]
        : [];
    });
}

function itemPlanDiscount(item: CaseQuoteItem, planDiscount: number) {
  return item.packageRelation ? planDiscount : 100;
}

function itemQuantityStep(item: CaseQuoteItem) {
  return item.packageRelation?.relationQuantity ?? 1;
}

function itemSubtotal(item: CaseQuoteItem, planDiscount: number) {
  return planProductSubtotal(
    item.unitPrice,
    itemPlanDiscount(item, planDiscount),
    item.quantity,
  );
}

function total(items: CaseQuoteItem[], planDiscount = 100) {
  return items.reduce(
    (amount, item) => amount + itemSubtotal(item, planDiscount),
    0,
  );
}

function aggregatedShareItems(items: CaseQuoteItem[], planDiscount: number) {
  const byDevice = new Map<
    string,
    { quantity: number; extendedAmount: number }
  >();
  for (const item of items) {
    const current = byDevice.get(item.deviceId) ?? {
      quantity: 0,
      extendedAmount: 0,
    };
    const unitPrice = planProductPrice(
      item.unitPrice,
      itemPlanDiscount(item, planDiscount),
    );
    current.quantity += item.quantity;
    current.extendedAmount += unitPrice * item.quantity;
    byDevice.set(item.deviceId, current);
  }
  return [...byDevice].map(([deviceId, amount]) => ({
    deviceId,
    quantity: amount.quantity,
    unitPrice: amount.extendedAmount / amount.quantity,
  }));
}

function waitForLocalStateWrite() {
  return new Promise<void>((resolve) => window.setTimeout(resolve, 20));
}

export function CaseLibraryPage() {
  const { message, modal } = App.useApp();
  const {
    state,
    commit,
    getCurrentState,
    hasPersistedCaseRehearsalQuote,
    navigateRoute,
  } = useCpq();
  const showcase = useMemo(() => createCaseShowcaseFixture(), []);
  const cases = useMemo(() => readCaseShowcaseMockCases(showcase), [showcase]);
  const [casePlanRelations] = useState(() =>
    readCaseShowcaseMockPlanRelations(showcase),
  );
  const casePlanIds = (record: CaseShowcaseRecord) =>
    casePlanRelations[record.id] ?? record.planIds;
  const canCreateQuote =
    canUseActivePermissionAction(state, 'quotes', 'quotes:quote.create') &&
    canUseActivePermissionAction(state, 'quotes', 'quotes:quote.edit') &&
    canUseActivePermissionAction(state, 'workspace', 'workspace:quote.read');
  const [selectedPlans, setSelectedPlans] = useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        cases.map((record) => [record.id, casePlanIds(record)[0] ?? '']),
      ),
  );
  const [cart, setCart] = useState<CaseCartRow[]>(() =>
    readCaseShowcaseMockCart()
      .map((entry) => {
        const record = cases.find((item) => item.id === entry.caseRecordId);
        const items = record ? planItems(showcase.catalog, entry.planId) : [];
        return record && items.length > 0
          ? {
              caseRecordId: entry.caseRecordId,
              planId: entry.planId,
              items,
            }
          : null;
      })
      .filter((row): row is CaseCartRow => Boolean(row)),
  );
  const [cartOpen, setCartOpen] = useState(false);
  const [reviewCaseId, setReviewCaseId] = useState<string | null>(null);
  const [draftsExpanded, setDraftsExpanded] = useState(false);
  const [caseQuery, setCaseQuery] = useState('');
  const [caseCategory, setCaseCategory] = useState('全部');
  const [categoryExpanded, setCategoryExpanded] = useState(false);
  const [drawerCaseId, setDrawerCaseId] = useState<string | null>(null);
  const [generatedCartShare, setGeneratedCartShare] = useState<{
    token: string;
    url: string;
  } | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const savingDraftRef = useRef(false);

  const caseById = (recordId: string | null) =>
    cases.find((record) => record.id === recordId);
  const drawerCase = caseById(drawerCaseId);
  const reviewRow = cart.find((row) => row.caseRecordId === reviewCaseId);
  const reviewCase = caseById(reviewRow?.caseRecordId ?? null);
  const reviewPackageGroups = new Map<string, CaseQuoteItem[]>();
  for (const item of reviewRow?.items ?? []) {
    const groupId = item.packageRelation?.packageId ?? 'other-devices';
    reviewPackageGroups.set(groupId, [
      ...(reviewPackageGroups.get(groupId) ?? []),
      item,
    ]);
  }
  const reviewPackages = [...reviewPackageGroups].map(([groupId, items]) => ({
    pkg: {
      id: groupId,
      name: items[0]?.packageRelation?.packageName ?? '其他设备',
    },
    devices: items.map((item) => ({
      device: item,
      link: { relationId: item.lineId, quantity: item.quantity },
    })),
    referenceTotal: total(items),
  }));
  const reviewDeviceLineCount = reviewPackages.reduce(
    (count, group) => count + group.devices.length,
    0,
  );
  const reviewDeviceQuantity = reviewPackages.reduce(
    (count, group) =>
      count +
      group.devices.reduce((quantity, { link }) => quantity + link.quantity, 0),
    0,
  );
  const reviewReferenceTotal = reviewPackages.reduce(
    (amount, group) => amount + group.referenceTotal,
    0,
  );
  const selectedPlanId = drawerCase
    ? (selectedPlans[drawerCase.id] ?? casePlanIds(drawerCase)[0] ?? '')
    : '';
  const selectedPlan = showcase.catalog.plans.find(
    (plan) => plan.id === selectedPlanId,
  );
  const caseCategories = useMemo(
    () =>
      [
        ...new Set(
          cases.flatMap((record) =>
            [
              record.category !== record.name ? record.category : '',
              ...record.tags.filter((tag) => tag !== record.name),
            ].filter((tag) => tag && tag !== '未分类'),
          ),
        ),
      ].sort((left, right) => left.localeCompare(right, 'zh-CN')),
    [cases],
  );
  const categoryOptions = useMemo(
    () => ['全部', ...caseCategories],
    [caseCategories],
  );
  // 收起时保留选中的分类，避免当前筛选被隐藏。
  const visibleCategoryOptions = categoryExpanded
    ? categoryOptions
    : categoryOptions.filter(
        (category, index) =>
          index < CASE_CATEGORY_PREVIEW_COUNT || category === caseCategory,
      );
  const localCaseRehearsalDrafts = useMemo(
    () =>
      Object.values(state.quotes)
        .filter((quote) => Boolean(quote.caseRehearsalSnapshot))
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)),
    [state.quotes],
  );
  const visibleCases = useMemo(() => {
    const query = caseQuery.trim().toLocaleLowerCase();
    return cases.filter((record) => {
      const matchesQuery =
        !query ||
        `${record.name} ${record.caseCode} ${record.caseId}`
          .toLocaleLowerCase()
          .includes(query);
      return matchesQuery && matchesCaseCategory(record, caseCategory);
    });
  }, [caseCategory, caseQuery, cases]);
  // 卡片摘要：方案数、设备件数、首选方案的参考金额。
  const caseCardStats = useMemo(() => {
    const stats = cases.map((record) => {
      const planIds = casePlanRelations[record.id] ?? record.planIds;
      const primaryPlanId = planIds[0];
      const items = primaryPlanId
        ? planItems(showcase.catalog, primaryPlanId)
        : [];
      return [
        record.id,
        {
          planCount: planIds.length,
          deviceCount: items.reduce((count, item) => count + item.quantity, 0),
          referenceTotal: items.reduce(
            (total, item) => total + item.unitPrice * item.quantity,
            0,
          ),
        },
      ] as const;
    });
    return new Map(stats);
  }, [casePlanRelations, cases, showcase.catalog]);

  const choosePlan = (record: CaseShowcaseRecord, planId: string) => {
    if (!casePlanIds(record).includes(planId)) return;
    setSelectedPlans((current) => ({ ...current, [record.id]: planId }));
    setCart((current) =>
      current.map((row) =>
        row.caseRecordId === record.id
          ? {
              caseRecordId: record.id,
              planId,
              items: planItems(showcase.catalog, planId),
            }
          : row,
      ),
    );
    setGeneratedCartShare(null);
  };

  const addCase = (record: CaseShowcaseRecord) => {
    const planId = selectedPlans[record.id] ?? casePlanIds(record)[0];
    if (!planId || cart.some((row) => row.caseRecordId === record.id)) return;
    setCart((current) => [
      ...current,
      {
        caseRecordId: record.id,
        planId,
        items: planItems(showcase.catalog, planId),
      },
    ]);
    setGeneratedCartShare(null);
  };

  const openReview = (recordId: string) => {
    if (!cart.some((row) => row.caseRecordId === recordId)) return;
    setReviewCaseId(recordId);
    setCartOpen(true);
  };

  // 购物车只存案例与方案，切页或刷新后按当前目录重建设备明细。
  useEffect(() => {
    saveCaseShowcaseMockCart(
      cart.map((row) => ({
        caseRecordId: row.caseRecordId,
        planId: row.planId,
      })),
    );
  }, [cart]);

  const removeCaseFromCart = (recordId: string) => {
    const nextCart = cart.filter((row) => row.caseRecordId !== recordId);
    setCart(nextCart);
    setGeneratedCartShare(null);
    if (reviewCaseId === recordId) {
      setReviewCaseId(nextCart[0]?.caseRecordId ?? null);
    }
  };

  const deleteLocalDraft = (quoteId: string) => {
    modal.confirm({
      title: '删除本地演练草稿',
      content:
        '草稿只保存在当前浏览器，删除后无法恢复，也不会影响案例库中的案例。',
      okText: '删除草稿',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        commit((current) => {
          const next = structuredClone(current);
          delete next.quotes[quoteId];
          next.openQuoteIds = next.openQuoteIds.filter((id) => id !== quoteId);
          if (next.openQuoteIds.length === 0) {
            // 至少保留一个可打开的空草稿，避免工作台失去活动报价。
            return createQuote(next);
          }
          if (next.activeQuoteId === quoteId) {
            next.activeQuoteId =
              next.openQuoteIds.at(-1) ?? next.openQuoteIds[0];
          }
          return next;
        });
        message.success('本地演练草稿已删除');
      },
    });
  };

  const createWorkspaceQuote = async (rows: CaseCartRow[]) => {
    if (savingDraftRef.current) return;
    savingDraftRef.current = true;
    setSavingDraft(true);
    try {
      if (!canCreateQuote) throw new Error('当前身份没有创建报价的权限');
      if (!rows.length) throw new Error('请先选择案例方案');
      const selected = rows.map((row) => {
        const record = caseById(row.caseRecordId);
        if (!record) throw new Error('所选案例已不可用，请重新选择');
        if (!row.items.length)
          throw new Error(`${record.name}的方案没有设备明细，无法进入报价`);
        const plan = showcase.catalog.plans.find(
          (candidate) => candidate.id === row.planId,
        );
        if (!plan) throw new Error('当前方案已不可用，请重新选择方案');
        return { row, record, plan };
      });
      const snapshots = selected.map(({ record, plan }) => ({
        caseRecordId: record.id,
        caseId: record.caseId,
        caseVersion: record.version,
        caseCode: record.caseCode,
        caseName: record.name,
        planId: plan.id,
        planName: plan.name,
      }));
      const firstSnapshot = snapshots[0];
      if (!firstSnapshot) throw new Error('请先选择案例方案');

      const current = getCurrentState();
      if (current.openQuoteIds.length >= 10)
        throw new Error('当前已打开 10 个报价，请先关闭一个再载入');
      const next = createQuote(current);
      const createdQuoteId = next.activeQuoteId;
      const quote = next.quotes[createdQuoteId];
      if (!quote) throw new Error('本地报价草稿创建失败');

      const items = [...quote.items];
      for (const { row, record, plan } of selected) {
        const selections: QuoteItemSelection[] = row.items.map((item) => {
          const step = itemQuantityStep(item);
          if (item.quantity < step || item.quantity % step !== 0)
            throw new Error(
              `${record.name}的设备 ${item.name} 数量须为每套 ${step} 件的整数倍`,
            );
          const device = showcase.catalog.devices.find(
            (record) => record.id === item.deviceId,
          );
          if (!device)
            throw new Error(`设备 ${item.name} 已不可用，未保存报价`);

          if (!item.packageRelation) {
            return {
              device,
              source: {
                sourceType: 'device',
                sourceId: device.id,
                sourceName: device.name,
                sourceCode: device.sku ?? undefined,
              },
              quantity: item.quantity,
            };
          }

          const pkg = showcase.catalog.packages.find(
            (record) => record.id === item.packageRelation?.packageId,
          );
          const relation = pkg?.productLinks.find(
            (record) => record.relationId === item.packageRelation?.relationId,
          );
          if (!pkg || !relation || relation.deviceId !== device.id)
            throw new Error(`设备 ${item.name} 的套餐关系已失效，未保存报价`);

          return {
            device,
            source: {
              sourceType: 'plan',
              sourceId: plan.id,
              sourceName: plan.name,
              ...(plan.code ? { sourceCode: plan.code } : {}),
              ...(item.packageRelation.packageRevisionId
                ? {
                    packageRevisionId: item.packageRelation.packageRevisionId,
                    packageNameSnapshot: item.packageRelation.packageName,
                  }
                : {}),
            },
            quantity: item.quantity,
            relation: {
              relationId: relation.relationId,
              packageId: pkg.id,
              replacementPoolId: relation.replacementPoolId ?? null,
              packageItemPrice: item.packageRelation.packageItemPrice,
              relationQuantity: item.packageRelation.relationQuantity,
              planId: plan.id,
              planDiscount: plan.discount,
            },
          };
        });
        // Merged quotes keep each case's lines distinct, even when plans match.
        const merged = mergeQuoteItemSelections(
          [],
          selections,
          next.nextSequence,
          quote.businessModeCode,
        );
        next.nextSequence = merged.nextSequence;
        items.push(
          ...merged.items.map((item) => ({
            ...item,
            caseRehearsalSource: {
              caseRecordId: record.id,
              caseId: record.caseId,
              caseCode: record.caseCode,
              caseName: record.name,
            },
          })),
        );
      }
      const createdAt = new Date().toISOString();
      const customers = [
        ...new Set(selected.map(({ record }) => record.targetCustomer)),
      ];
      next.quotes[createdQuoteId] = {
        ...quote,
        title:
          selected.length === 1
            ? `${selected[0].record.name} · ${selected[0].plan.name}`
            : `${selected.length} 个案例合并报价`,
        customer: {
          ...quote.customer,
          name: customers.length === 1 ? customers[0] : '',
        },
        items: items.map((item) =>
          item.planId &&
          item.packageRelationId &&
          item.packageItemPriceSnapshot !== undefined &&
          item.planDiscountSnapshot !== undefined
            ? {
                ...item,
                planProductSubtotalSnapshot: planProductSubtotal(
                  item.packageItemPriceSnapshot,
                  item.planDiscountSnapshot,
                  item.quantity,
                ),
              }
            : item,
        ),
        dirty: true,
        updatedAt: createdAt,
        caseRehearsalSnapshot: {
          kind: 'case-rehearsal',
          ...firstSnapshot,
          ...(snapshots.length > 1 ? { cases: snapshots } : {}),
          createdAt,
        },
      };
      const saved = saveDraft(
        { ...next, workspacePicker: null },
        createdQuoteId,
      );
      saved.quotes[createdQuoteId].updatedAt = createdAt;
      commit(() => saved);
      let persisted = false;
      for (let attempt = 0; attempt < 50 && !persisted; attempt += 1) {
        await waitForLocalStateWrite();
        persisted = await hasPersistedCaseRehearsalQuote(createdQuoteId);
      }
      if (!persisted) throw new Error('本地存储未确认案例报价，已取消保存回执');

      if (await openSavedQuote(createdQuoteId)) {
        setCartOpen(false);
        setReviewCaseId(null);
        message.success('案例报价已进入本地报价工作台');
      } else {
        message.warning('草稿已保存，可从“本地演练草稿”继续打开');
      }
    } catch (cause) {
      message.error(
        cause instanceof Error
          ? cause.message
          : '案例报价保存失败，未生成本地草稿',
      );
    } finally {
      savingDraftRef.current = false;
      setSavingDraft(false);
    }
  };

  const openSavedQuote = async (quoteId: string): Promise<boolean> => {
    const current = getCurrentState();
    if (!current.quotes[quoteId]?.caseRehearsalSnapshot) {
      message.error('本地案例报价草稿不存在或已失效');
      return false;
    }
    if (
      !canUseActivePermissionAction(
        current,
        'workspace',
        'workspace:quote.read',
      )
    ) {
      message.error('当前身份没有查看报价工作台的权限');
      return false;
    }
    if (
      !current.openQuoteIds.includes(quoteId) &&
      current.openQuoteIds.length >= 10
    ) {
      message.error('当前已打开 10 个报价，请先关闭一个再载入');
      return false;
    }
    return navigateRoute((latest) => ({
      activeView: 'workspace',
      activeQuoteId: quoteId,
      activeQuoteVersionId: undefined,
      workspacePicker: null,
      openQuoteIds: latest.openQuoteIds.includes(quoteId)
        ? latest.openQuoteIds
        : [...latest.openQuoteIds, quoteId],
    }));
  };

  const generateCartShare = () => {
    const cases = cart.flatMap((row) => {
      const record = caseById(row.caseRecordId);
      const plan = showcase.catalog.plans.find(
        (candidate) => candidate.id === row.planId,
      );
      return record && plan
        ? [
            {
              caseId: record.caseId,
              planId: plan.id,
              items: aggregatedShareItems(row.items, plan.discount),
            },
          ]
        : [];
    });
    if (cases.length !== cart.length) {
      message.error('购物车中有不可用的案例方案，暂时无法分享');
      return;
    }
    try {
      const token = createCaseShowcaseCartShareToken({ cases });
      const url = `${window.location.origin}${window.location.pathname}#share/${token}`;
      setGeneratedCartShare({ token, url });
      message.success('购物车分享链接已生成');
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '购物车分享链接生成失败',
      );
    }
  };

  const copyCartShare = async () => {
    if (!generatedCartShare) return;
    try {
      await navigator.clipboard.writeText(generatedCartShare.url);
      message.success('购物车分享链接已复制');
    } catch {
      message.warning('浏览器未允许自动复制，请手动复制分享链接');
    }
  };

  const openCartShare = () => {
    if (!generatedCartShare) return;
    window.location.hash = `share/${generatedCartShare.token}`;
  };

  return (
    <section className={styles.case20Page}>
      <h2 className={styles.case20ScreenReaderOnly}>案例库</h2>

      <section className={styles.case20Filters} aria-label="案例筛选">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="搜索案例名称或编号"
          aria-label="搜索案例名称或编号"
          value={caseQuery}
          onChange={(event) => setCaseQuery(event.target.value)}
        />
        <fieldset
          className={styles.case20CategoryFilters}
          aria-label="案例分类"
        >
          <span>分类标签</span>
          <div className={styles.case20CategoryOptions}>
            {visibleCategoryOptions.map((category) => (
              <Button
                key={category}
                size="small"
                aria-pressed={caseCategory === category}
                className={
                  caseCategory === category ? styles.case20CategoryActive : ''
                }
                onClick={() => setCaseCategory(category)}
              >
                {category}
              </Button>
            ))}
            {categoryOptions.length > CASE_CATEGORY_PREVIEW_COUNT ? (
              <Button
                size="small"
                type="link"
                aria-expanded={categoryExpanded}
                onClick={() => setCategoryExpanded((value) => !value)}
              >
                {categoryExpanded
                  ? '收起'
                  : `更多 +${categoryOptions.length - CASE_CATEGORY_PREVIEW_COUNT}`}
              </Button>
            ) : null}
          </div>
        </fieldset>
        <span className={styles.case20ResultsCount} aria-live="polite">
          {visibleCases.length} / {cases.length} 个案例
        </span>
      </section>

      <section
        className={styles.case20LocalDrafts}
        aria-labelledby="case20-local-drafts-title"
      >
        <header className={styles.case20LocalDraftsHeader}>
          <h3 id="case20-local-drafts-title">本地演练草稿</h3>
          <span>{localCaseRehearsalDrafts.length} 份</span>
          {localCaseRehearsalDrafts.length ? (
            <Button
              size="small"
              aria-expanded={draftsExpanded}
              onClick={() => setDraftsExpanded((expanded) => !expanded)}
            >
              {draftsExpanded ? '收起草稿' : '展开草稿'}
            </Button>
          ) : null}
        </header>
        {localCaseRehearsalDrafts.length && draftsExpanded ? (
          <ul className={styles.case20LocalDraftList}>
            {localCaseRehearsalDrafts.map((quote) => {
              const snapshot = quote.caseRehearsalSnapshot;
              if (!snapshot) return null;
              const sources = snapshot.cases ?? [snapshot];
              const planNames = [
                ...new Set(sources.map((source) => source.planName)),
              ].join('、');
              return (
                <li className={styles.case20LocalDraft} key={quote.quoteId}>
                  <div className={styles.case20LocalDraftDetails}>
                    <strong>
                      {sources.length === 1
                        ? snapshot.caseName
                        : `${sources.length} 个案例合并报价`}
                    </strong>
                    <small>
                      {`${sources
                        .map((source) => source.caseId)
                        .join(
                          '、',
                        )}${planNames ? ` · ${planNames}` : ''} · 更新于 `}
                      {new Intl.DateTimeFormat('zh-CN', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      }).format(new Date(quote.updatedAt))}
                    </small>
                  </div>
                  <Button
                    aria-label={`打开本地演练草稿：${quote.title}`}
                    onClick={() => openSavedQuote(quote.quoteId)}
                  >
                    继续编辑
                  </Button>
                  <Button
                    danger
                    aria-label={`删除本地演练草稿：${quote.title}`}
                    onClick={() => deleteLocalDraft(quote.quoteId)}
                  >
                    删除
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : localCaseRehearsalDrafts.length ? (
          <p className={styles.case20LocalDraftEmpty}>
            草稿已收起，展开后可继续编辑或删除。
          </p>
        ) : (
          <p className={styles.case20LocalDraftEmpty}>
            暂无草稿。将案例导入报价工作台后，可从这里继续编辑。
          </p>
        )}
      </section>

      {visibleCases.length ? (
        <section className={styles.case20Grid} aria-label="案例列表">
          {visibleCases.map((record) => {
            const isAdded = cart.some((row) => row.caseRecordId === record.id);
            const hasAvailablePlan = casePlanIds(record).length > 0;
            const stats = caseCardStats.get(record.id);
            return (
              <article className={styles.case20Card} key={record.id}>
                <HoverImagePreview
                  sources={[record.heroImage, ...record.gallery]}
                  alt={`${record.name} 案例效果图`}
                  frameClassName={styles.case20CardImage}
                  imageStyle={{ objectFit: 'cover' }}
                />
                <div className={styles.case20CardBody}>
                  <small>{record.caseCode ?? record.caseId}</small>
                  <h3>{record.name}</h3>
                  <p className={styles.case20CardMeta}>
                    {stats && stats.planCount > 0 ? (
                      <>
                        <span>{stats.planCount} 个方案</span>
                        <span>{stats.deviceCount} 件设备</span>
                        <span>
                          参考价 {formatMoney(stats.referenceTotal)}/月
                        </span>
                      </>
                    ) : (
                      <span>暂无关联方案</span>
                    )}
                  </p>
                  <div className={styles.case20Actions}>
                    <Button
                      aria-label={`查看${record.name}的方案与设备`}
                      onClick={() => setDrawerCaseId(record.id)}
                    >
                      查看方案与设备
                    </Button>
                    <Button
                      className={isAdded ? styles.case20CartAdded : undefined}
                      icon={
                        isAdded ? (
                          <CheckCircleFilled aria-hidden="true" />
                        ) : undefined
                      }
                      type={isAdded ? 'default' : 'primary'}
                      disabled={!hasAvailablePlan}
                      onClick={() =>
                        isAdded ? openReview(record.id) : addCase(record)
                      }
                    >
                      {isAdded
                        ? '查看购物车'
                        : hasAvailablePlan
                          ? '加入购物车'
                          : '暂无可用方案'}
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <div className={styles.case20NoResults} role="status">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="没有找到匹配的案例"
          />
          <Button
            onClick={() => {
              setCaseQuery('');
              setCaseCategory('全部');
            }}
          >
            清除搜索和分类
          </Button>
        </div>
      )}

      <aside
        className={`${styles.case20Cart} ${styles.case20CartCollapsed}`}
        aria-label="方案购物车"
      >
        {cart.length > 0 ? (
          <div id="case-plan-cart-content" className={styles.case20CartContent}>
            <div className={styles.case20CartHead}>
              <strong>方案购物车</strong>
              <span>{cart.length} 个案例</span>
            </div>
            <div className={styles.case20CartList}>
              {cart.map((row) => {
                const record = caseById(row.caseRecordId);
                const plan = showcase.catalog.plans.find(
                  (candidate) => candidate.id === row.planId,
                );
                if (!record || !plan) return null;
                return (
                  <div className={styles.case20CartItem} key={record.id}>
                    <HoverImagePreview
                      sources={[record.heroImage, ...record.gallery]}
                      alt={`${record.name} 案例效果图`}
                      frameClassName={styles.case20CartItemImage}
                    />
                    <span>
                      <b>{record.name}</b>
                      <small>
                        {plan.name} · {row.items.length} 项设备
                      </small>
                    </span>
                    <Button
                      className={styles.case20CartRemove}
                      aria-label={`移除${record.name}案例`}
                      onClick={() => removeCaseFromCart(record.id)}
                    >
                      移除
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className={styles.case20CartActions}>
              <Button
                icon={<ShareAltOutlined aria-hidden="true" />}
                aria-label="分享购物车"
                disabled={!cart.length}
                onClick={generateCartShare}
              >
                分享购物车
              </Button>
              <Button
                type="primary"
                disabled={!cart.length}
                onClick={() => cart[0] && openReview(cart[0].caseRecordId)}
              >
                查看明细
              </Button>
            </div>
          </div>
        ) : null}
        <Button
          className={styles.case20CartToggle}
          aria-label={`购物车 ${cart.length} 个案例`}
          aria-haspopup="dialog"
          onClick={() => {
            const row = reviewRow ?? cart[0];
            if (row) openReview(row.caseRecordId);
            else message.info('购物车还是空的，先选择案例加入购物车');
          }}
        >
          <ShoppingCartOutlined />
          {cart.length > 0 ? <b>{cart.length}</b> : null}
        </Button>
      </aside>

      <Modal
        centered
        width="min(1468px, calc(100vw - 32px))"
        open={cartOpen}
        aria-labelledby="case20-review-title"
        className={styles.case20ReviewModal}
        title={
          <div className={styles.case20ReviewHead}>
            <div className={styles.case20ReviewHeadCopy}>
              <h2 id="case20-review-title">购物车明细</h2>
              <p>点击案例切换设备清单，选择方案后可直接分享或进入报价工作台</p>
            </div>
            <span className={styles.case20ReviewHeadCount}>
              已选 {cart.length} 个案例
            </span>
          </div>
        }
        footer={
          <div className={styles.case20ReviewFooter}>
            <span className={styles.case20ReviewFooterHint}>
              {!canCreateQuote
                ? '当前身份没有创建报价或进入报价工作台的权限。'
                : reviewCase
                  ? `进入工作台仅导入当前案例「${reviewCase.name}」，设备编辑在工作台完成。`
                  : '先选择案例加入购物车。'}
            </span>
            <div className={styles.case20ReviewFooterActions}>
              <Button
                disabled={!reviewCase}
                onClick={() => reviewCase && removeCaseFromCart(reviewCase.id)}
              >
                移除当前案例
              </Button>
              <Button onClick={() => setCartOpen(false)}>继续选案例</Button>
              <Button
                icon={<ShareAltOutlined aria-hidden="true" />}
                disabled={!cart.length}
                onClick={generateCartShare}
              >
                分享购物车
              </Button>
              <Button
                type="primary"
                disabled={!canCreateQuote || !reviewRow?.items.length}
                loading={savingDraft}
                onClick={() =>
                  reviewRow && void createWorkspaceQuote([reviewRow])
                }
              >
                进入报价工作台
              </Button>
            </div>
          </div>
        }
        onCancel={() => setCartOpen(false)}
      >
        {reviewRow && reviewCase ? (
          <div className={styles.case20ReviewBody}>
            <fieldset className={styles.case20ReviewTabs} aria-label="已选案例">
              {cart.map((row) => {
                const record = caseById(row.caseRecordId);
                const plan = showcase.catalog.plans.find(
                  (candidate) => candidate.id === row.planId,
                );
                if (!record) return null;
                const active = record.id === reviewCase.id;
                return (
                  <button
                    type="button"
                    key={record.id}
                    className={`${styles.case20ReviewTab} ${active ? styles.case20ReviewTabActive : ''}`}
                    aria-pressed={active}
                    onClick={() => {
                      setReviewCaseId(record.id);
                    }}
                  >
                    <img
                      className={styles.case20ReviewTabImage}
                      src={record.heroImage}
                      alt=""
                    />
                    <span className={styles.case20ReviewTabCopy}>
                      <small>{record.caseId}</small>
                      <b>{record.name}</b>
                      <small>{plan?.name ?? '尚未选择方案'}</small>
                    </span>
                  </button>
                );
              })}
            </fieldset>

            <section
              className={styles.case20ReviewCurrent}
              aria-label={`${reviewCase.name}方案与设备清单`}
            >
              <fieldset
                className={`${styles.case20PlanChoices} ${styles.case20ReviewPlanChoices}`}
                aria-label="当前案例方案选择"
              >
                {casePlanIds(reviewCase).map((planId) => {
                  const plan = showcase.catalog.plans.find(
                    (record) => record.id === planId,
                  );
                  return plan ? (
                    <button
                      type="button"
                      key={plan.id}
                      className={
                        plan.id === reviewRow.planId
                          ? styles.case20PlanActive
                          : ''
                      }
                      aria-pressed={plan.id === reviewRow.planId}
                      onClick={() => choosePlan(reviewCase, plan.id)}
                    >
                      <b>{plan.name}</b>
                      <span>
                        专属折扣 {plan.discount}% ·{' '}
                        {formatMoney(
                          total(
                            planItems(showcase.catalog, plan.id),
                            plan.discount,
                          ),
                        )}
                      </span>
                    </button>
                  ) : null;
                })}
              </fieldset>

              <section
                className={styles.case20ReviewSummary}
                aria-label="清单摘要"
              >
                <div className={styles.case20ReviewSummaryStat}>
                  <span>套餐</span>
                  <strong>{reviewPackages.length}</strong>
                </div>
                <div className={styles.case20ReviewSummaryStat}>
                  <span>设备明细</span>
                  <strong>{reviewDeviceLineCount}</strong>
                </div>
                <div className={styles.case20ReviewSummaryStat}>
                  <span>设备数量</span>
                  <strong>{reviewDeviceQuantity}</strong>
                </div>
                <div className={styles.case20ReviewSummaryStat}>
                  <span>方案参考金额</span>
                  <strong>{formatMoney(reviewReferenceTotal)}</strong>
                </div>
              </section>

              {reviewPackages.length ? (
                <div className={styles.case20ReviewPackageGrid}>
                  {reviewPackages.map(({ pkg, devices, referenceTotal }) => {
                    const midpoint = Math.ceil(devices.length / 2);
                    const columns =
                      devices.length > 14
                        ? [devices.slice(0, midpoint), devices.slice(midpoint)]
                        : [devices];
                    return (
                      <section
                        className={styles.case20ReviewPackage}
                        key={pkg.id}
                        aria-label={`${pkg.name}套餐`}
                      >
                        <header className={styles.case20ReviewPackageHead}>
                          <div>
                            <h4>{pkg.name}</h4>
                            <small>
                              {devices.length} 条设备明细 ·{' '}
                              {devices.reduce(
                                (quantity, { link }) =>
                                  quantity + link.quantity,
                                0,
                              )}{' '}
                              件设备
                            </small>
                          </div>
                          <div>
                            <small>套餐参考金额</small>
                            <strong>{formatMoney(referenceTotal)}</strong>
                          </div>
                        </header>
                        <div className={styles.case20ReviewDeviceColumns}>
                          {columns.map((column, columnIndex) => (
                            <table
                              className={styles.case20ReviewPackageDevices}
                              key={column[0]?.link.relationId ?? columnIndex}
                              aria-label={
                                columns.length > 1
                                  ? `${pkg.name}设备清单第 ${columnIndex + 1} 列`
                                  : `${pkg.name}设备清单`
                              }
                            >
                              <thead>
                                <tr className={styles.case20ReviewDeviceHeader}>
                                  <th scope="col">设备类别</th>
                                  <th scope="col">设备名称</th>
                                  <th scope="col">数量</th>
                                </tr>
                              </thead>
                              <tbody>
                                {column.map(({ device, link }) => (
                                  <tr
                                    className={styles.case20ReviewDeviceRow}
                                    key={link.relationId}
                                  >
                                    <td
                                      className={
                                        styles.case20ReviewDeviceCategory
                                      }
                                    >
                                      <span>{device.category}</span>
                                    </td>
                                    <td
                                      className={styles.case20ReviewDeviceName}
                                      title={device.name}
                                    >
                                      {device.name}
                                    </td>
                                    <td
                                      className={
                                        styles.case20ReviewDeviceQuantity
                                      }
                                    >
                                      {link.quantity}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="当前方案暂无套餐设备明细"
                />
              )}
            </section>
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="尚未加入案例方案"
          />
        )}
      </Modal>

      <Modal
        centered
        width="min(1120px, calc(100vw - 32px))"
        open={Boolean(drawerCase)}
        title={drawerCase ? `${drawerCase.name} · 方案与设备` : ''}
        className={styles.case20CaseModal}
        footer={
          drawerCase ? (
            <div className={styles.case20CaseFooter}>
              <div className={styles.case20CaseTotal}>
                <span>{selectedPlan?.name} · 方案报价</span>
                <strong>
                  {formatMoney(
                    total(
                      planItems(showcase.catalog, selectedPlanId),
                      selectedPlan?.discount ?? 100,
                    ),
                  )}
                </strong>
              </div>
              <div className={styles.case20CaseFooterActions}>
                {cart.some((row) => row.caseRecordId === drawerCase.id) ? (
                  <Button
                    className={styles.case20CartAdded}
                    icon={<CheckCircleFilled aria-hidden="true" />}
                    type="default"
                    onClick={() => {
                      setDrawerCaseId(null);
                      openReview(drawerCase.id);
                    }}
                  >
                    查看购物车
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    disabled={casePlanIds(drawerCase).length === 0}
                    onClick={() => {
                      addCase(drawerCase);
                      setDrawerCaseId(null);
                    }}
                  >
                    加入购物车
                  </Button>
                )}
              </div>
            </div>
          ) : null
        }
        onCancel={() => setDrawerCaseId(null)}
      >
        {drawerCase ? (
          <div className={styles.case20CaseBody}>
            <div className={styles.case20CaseIntro}>
              <small>
                {drawerCase.caseId} · {drawerCase.caseCode}
              </small>
              <h3>选择方案并核对设备数量</h3>
              <p>设备按方案展示；需要调整时，在购物车中进入报价工作台。</p>
            </div>
            <fieldset
              className={styles.case20PlanChoices}
              aria-label="案例方案选择"
            >
              {casePlanIds(drawerCase).map((planId) => {
                const plan = showcase.catalog.plans.find(
                  (record) => record.id === planId,
                );
                return plan ? (
                  <button
                    type="button"
                    className={
                      planId === selectedPlanId ? styles.case20PlanActive : ''
                    }
                    aria-pressed={planId === selectedPlanId}
                    key={plan.id}
                    onClick={() => choosePlan(drawerCase, plan.id)}
                  >
                    <small>
                      {planId === selectedPlanId ? '当前方案' : '可选方案'}
                    </small>
                    <b>{plan.name}</b>
                    <span>专属折扣 {plan.discount}%</span>
                    <strong>
                      {formatMoney(
                        total(
                          planItems(showcase.catalog, plan.id),
                          plan.discount,
                        ),
                      )}
                    </strong>
                  </button>
                ) : null;
              })}
              {casePlanIds(drawerCase).length === 0 ? (
                <div className={styles.case20CaseEmptyPlans}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="该案例尚未关联可用方案"
                  />
                  <p>
                    可在「后台 →
                    案例管理」中编辑该案例并关联方案，关联后即可加入购物车。
                  </p>
                </div>
              ) : null}
            </fieldset>
            {planItems(showcase.catalog, selectedPlanId).length ? (
              <div className={styles.case20DeviceList}>
                {planItems(showcase.catalog, selectedPlanId).map((item) => (
                  <div className={styles.case20Device} key={item.lineId}>
                    <span>
                      <b>{item.name}</b>
                      <small>{item.sku}</small>
                    </span>
                    <span className={styles.case20DeviceQuantity}>
                      数量 {item.quantity}
                    </span>
                    <strong className={styles.case20DeviceAmount}>
                      {formatMoney(
                        itemSubtotal(item, selectedPlan?.discount ?? 100),
                      )}
                    </strong>
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="当前方案暂无设备明细"
              />
            )}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(generatedCartShare)}
        title="分享购物车明细"
        className={styles.case20CartShareModal}
        footer={
          generatedCartShare ? (
            <div className={styles.case20CartShareActions}>
              <Button
                icon={<CopyOutlined aria-hidden="true" />}
                onClick={copyCartShare}
              >
                复制链接
              </Button>
              <Button type="primary" onClick={openCartShare}>
                打开购物车分享页
              </Button>
            </div>
          ) : null
        }
        onCancel={() => setGeneratedCartShare(null)}
      >
        {generatedCartShare ? (
          <div className={styles.case20CartShareBody}>
            <p>
              {['127.0.0.1', 'localhost'].includes(window.location.hostname)
                ? '当前是本机预览链接，仅能在本机查看。前端发布到外部可访问地址后，可将链接发给客户查看购物车明细。'
                : '外部用户可通过链接查看购物车中各案例的方案与设备明细。'}
            </p>
            <Input
              value={generatedCartShare.url}
              readOnly
              aria-label="购物车分享链接"
            />
          </div>
        ) : null}
      </Modal>
    </section>
  );
}
